import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Send, Sparkles } from "lucide-react";
import { color, radius, space, motion as motionTokens, useListItemMotion } from "@tokens/index";
import { Avatar, IconButton, Text, TextInput } from "../../primitives";
import { summarizeRouting, type QueueNameResolver } from "../workflowRecipients";
import type { WorkItem } from "../../../experience-shell/workflows";
import { formatRelativeTime } from "./formatters";
import { hairline } from "./expandMotion";

// ---------------------------------------------------------------------------
// Message stream
// ---------------------------------------------------------------------------

function MessageBubble({
  item,
  isMine,
  resolveQueueName,
  index,
}: {
  readonly item: WorkItem;
  readonly isMine: boolean;
  readonly resolveQueueName: QueueNameResolver;
  readonly index: number;
}): ReactElement {
  const arrival = useListItemMotion(index, false);
  const routing = summarizeRouting(
    {
      requestedByQueueId: item.requestedByQueueId ?? null,
      requestedByQueueName: item.requestedByQueueName ?? null,
      visibleToQueueIds: item.visibleToQueueIds,
    },
    resolveQueueName,
  );
  const author = item.createdByDisplayName ?? item.createdBy ?? "Unknown";
  const settled = item.completedAt !== null;

  return (
    <motion.div
      {...arrival}
      style={{
        display: "flex",
        flexDirection: isMine ? "row-reverse" : "row",
        alignItems: "flex-end",
        gap: space[2],
        maxWidth: "88%",
        alignSelf: isMine ? "flex-end" : "flex-start",
        flexShrink: 0,
        opacity: settled ? 0.72 : 1,
        minWidth: 0,
      }}
    >
      {!isMine && (
        <div style={{ paddingBottom: space[1], flexShrink: 0 }}>
          <Avatar alt={author} size="sm" />
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: space[1],
          alignItems: isMine ? "flex-end" : "flex-start",
          minWidth: 0,
        }}
      >
        {!isMine && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: space[2],
              paddingInline: space[1],
              minWidth: 0,
            }}
          >
            <Text role="caption" weight="medium" tone="muted" truncate>
              {author}
            </Text>
            <Text role="caption" tone="subtle">
              {formatRelativeTime(item.createdAt)}
            </Text>
          </div>
        )}
        <div
          style={{
            padding: `${space[2]} ${space[3]}`,
            borderRadius: isMine
              ? `${radius.lg} ${radius.lg} ${radius.sm} ${radius.lg}`
              : `${radius.lg} ${radius.lg} ${radius.lg} ${radius.sm}`,
            background: isMine ? color.bg.selected : color.bg.surface2,
            color: color.fg.default,
            wordBreak: "break-word",
            minWidth: 0,
          }}
        >
          <Text as="span" role="body" style={{ color: "inherit" }}>
            {item.body}
          </Text>
        </div>
        {(routing.recipients !== null || settled) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: space[2],
              flexWrap: "wrap",
              paddingInline: space[1],
              justifyContent: isMine ? "flex-end" : "flex-start",
              minWidth: 0,
            }}
          >
            {routing.recipients !== null && (
              <Text role="caption" size="xs" tone="subtle" truncate>
                {routing.recipients.label}
              </Text>
            )}
            {settled && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: space[1],
                  color: color.accent.success,
                }}
              >
                <Check size={11} strokeWidth={2.5} aria-hidden="true" />
                <Text role="caption" size="xs" style={{ color: "inherit" }}>
                  settled
                </Text>
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function MessageStream({
  items,
  currentUserId,
  resolveQueueName,
  emptyHint,
}: {
  readonly items: readonly WorkItem[];
  readonly currentUserId: string | undefined;
  readonly resolveQueueName: QueueNameResolver;
  readonly emptyHint: string;
}): ReactElement {
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: space[3],
          padding: space[6],
          textAlign: "center",
        }}
      >
        <Sparkles size={24} strokeWidth={1.5} color={color.accent.primary} aria-hidden="true" />
        <Text role="body" tone="muted">
          {emptyHint}
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overscrollBehavior: "contain",
        display: "flex",
        flexDirection: "column",
        gap: space[3],
        padding: space[4],
      }}
    >
      {/* Messages on hand when the stream opens animate nothing; a message
          that arrives later (send, realtime) fades in. */}
      <AnimatePresence initial={false}>
        {items.map((item, index) => (
          <MessageBubble
            key={item.id}
            item={item}
            index={index}
            isMine={currentUserId != null && item.createdBy === currentUserId}
            resolveQueueName={resolveQueueName}
          />
        ))}
      </AnimatePresence>
      <div ref={bottomRef} style={{ height: space[1], flexShrink: 0 }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composers
// ---------------------------------------------------------------------------

/**
 * Text-input well that hosts a bare input and a trailing send control.
 * Flat surface, hairline edge. The focus treatment is the stylesheet's
 * `data-focus-style="input"` rule, applied while the inner field has focus.
 */
export const ComposerWell = forwardRef<HTMLDivElement, { readonly children: ReactNode }>(
  function ComposerWell({ children }, ref) {
    return (
      <div
        ref={ref}
        data-focus-style="input"
        style={{
          display: "flex",
          alignItems: "center",
          gap: space[2],
          background: color.bg.surface,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: radius.lg,
          padding: `${space[1]} ${space[1]} ${space[1]} ${space[3]}`,
          minWidth: 0,
          transitionProperty: "border-color, box-shadow",
          transitionDuration: motionTokens.durations.fast,
          transitionTimingFunction: motionTokens.easings.standard,
        }}
      >
        {children}
      </div>
    );
  },
);

export function SendButton({
  enabled,
  label,
  onClick,
}: {
  readonly enabled: boolean;
  readonly label: string;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <IconButton
      icon={Send}
      size="md"
      variant={enabled ? "primary" : "ghost"}
      disabled={!enabled}
      aria-label={label}
      onClick={onClick}
    />
  );
}

export function MessageComposer({
  placeholder,
  disabled,
  onSend,
}: {
  readonly placeholder: string;
  readonly disabled: boolean;
  readonly onSend: (body: string) => void;
}): ReactElement {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const canSend = draft.trim().length > 0 && !disabled;

  const handleSend = useCallback(() => {
    const body = draft.trim();
    if (body.length === 0 || disabled) return;
    onSend(body);
    setDraft("");
    inputRef.current?.focus();
  }, [draft, disabled, onSend]);

  return (
    <ComposerWell>
      <TextInput
        ref={inputRef}
        variant="bare"
        fullWidth
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        disabled={disabled}
        aria-label="Message"
      />
      <SendButton enabled={canSend} label="Send message" onClick={handleSend} />
    </ComposerWell>
  );
}

/**
 * Footer strip that hosts a composer under a stream. The strip is the lowest
 * region of its pane and sits on the natural floor of the panel.
 */
export function ComposerFooter({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <div
      style={{
        padding: `${space[3]} ${space[4]} ${space[4]}`,
        borderTop: hairline,
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}
